import React, { useEffect, useRef, useState } from "react";
import { wormColors } from "core/src/core/constants.js";

import { useIsTouch } from "../../../shared/useResponsive";
import KeyPicker from "../../../shared/key-picker/KeyPicker";
import { useKeyChainFocus } from "../../../shared/useKeyChainFocus";
import Equipment from "../../shop/Equipment";
import type { useLocalRoster } from "./hooks";
import { Swatches } from "./parts";
import s from "./PlayerMenu.module.scss";

// Your player, as one block: your name and steering keys (none on a touch
// device) on one line, then what you wear, the colour leading it (a click opens
// the colours over the cards; the other cards open their Store tab). The
// navbar's player menu shows all of it; the profile page only what you wear.
export default function PlayerSettings({ roster, onShop, size, className, fields = true }: {
  roster: ReturnType<typeof useLocalRoster>;
  onShop: (tab?: string) => void;
  size?: "lg";
  className?: string;
  fields?: boolean; // the name and keys line (the profile page shows only what you wear)
}) {
  const me = roster.players[0];
  const hex = (wormColors as Record<string, string>)[me.colorId];
  const touch = useIsTouch(); // a touch device, not a narrow window: a small PC window keeps its keys
  const { advance, focusToken } = useKeyChainFocus();
  const [colourOpen, setColourOpen] = useState(false);
  // The colour row closes on a pick, and on a click anywhere but it (or the
  // colour card, which toggles it itself).
  const colourRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!colourOpen) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element;
      if (colourRef.current?.contains(t) || t.closest?.('[aria-label="Colour"]')) return;
      setColourOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [colourOpen]);

  const keys = touch ? null : (
    <span className={s.keys} aria-label="Steering keys">
      <span className={s.key}><KeyPicker currentKey={me.left ?? undefined} onKeyPicked={(k: string) => { roster.setKey(0, "left", k); advance("0", "left"); }} autoSelectToken={focusToken("0", "left")} /></span>
      <span className={s.key}><KeyPicker currentKey={me.right ?? undefined} onKeyPicked={(k: string) => { roster.setKey(0, "right", k); advance("0", "right"); }} autoSelectToken={focusToken("0", "right")} /></span>
    </span>
  );
  return (
    <div className={`${s.settings} ${className || ""}`}>
      {fields && <div className={s.pair}>
        <label className={s.field}>
          <small className={s.label}>Name</small>
          <input className={s.input} value={me.name} maxLength={16} onChange={(e) => roster.setName(0, e.target.value)} aria-label="Your name" spellCheck={false} />
        </label>
        {keys && <div className={s.field}><small className={s.label}>Keybindings</small>{keys}</div>}
      </div>}
      <div className={s.wearWrap}>
        {colourOpen && (
          <div className={s.colourPop} ref={colourRef}>
            <Swatches player={me} players={roster.players.slice(0, 1)} onPick={(c) => { roster.setColor(0, c); setColourOpen(false); }} />
          </div>
        )}
        <Equipment flag={me.flag} onFlag={(f) => roster.setFlag(0, f)} onShop={onShop} size={size} colour={{ color: hex, onClick: () => setColourOpen((v) => !v) }} />
      </div>
    </div>
  );
}
